// ═══════════════════════════════════════════════════════════════════════════
// shared.js — the ONE Position shape every Silas advisor emits (ROADMAP.md
// §1.2 "The Decision object — one shape for all three advisors"). Stage 1
// (position objects, exposure only — ROADMAP.md §1.2 Build stages, row 1)
// fills `exposure` and `currentProtection` only; everything downstream of
// that (economics, market context, action, confidence, reasons) is Stage
// 2+ and stays null here on purpose — a filled-in field this stage does not
// compute would be a fabricated recommendation, exactly what §1.2's
// non-negotiables forbid ("AI never chooses the recommendation" extends to
// "code never fills in a stage it hasn't reached").
//
// PURE. No fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

// ROADMAP.md §2.1 — the provenance ladder. A number may never be shown at a
// higher (lower-numbered) tier than its weakest input.
export const TIER = {
  FARMER_VERIFIED: 1,
  FARM_OBSERVED: 2,
  FARM_DERIVED: 3,
  BENCHMARK: 4,
  FALLBACK_ESTIMATE: 5,
  UNKNOWN: null,
};

// The weakest (highest-numbered, or null/UNKNOWN) tier among a list of tiers.
// null poisons the result — an unknown input makes the composite unknown,
// never silently the best of the others. Mirrors the CLAUDE.md/ROADMAP
// "mixing rule": a total is reported as the weakest tier present.
export function weakestTier(tiers) {
  const list = (tiers || []).filter(t => t !== undefined);
  if (!list.length) return TIER.UNKNOWN;
  if (list.some(t => t === TIER.UNKNOWN)) return TIER.UNKNOWN;
  return Math.max(...list);
}

// One Position object. `exposure`/`currentProtection` are Stage-1 fields;
// everything else is left explicitly null (not omitted — a missing key vs a
// null key look identical to a consumer that forgets to check, so every
// field the eventual Decision object carries is declared here even before
// this stage fills it, per ROADMAP.md §1.2's Decision shape).
export function makePosition({
  farm, commodity, cropYear = null, riskType = null,
  exposure = null, currentProtection = null,
  tier = TIER.UNKNOWN, warnings = [], asOf = null,
}) {
  return {
    farm, commodity, cropYear, riskType,
    exposure,
    currentProtection,
    // Stage 2+ — not computed by this build. Never invented.
    economicBreakEven: null,
    currentMarket: null,
    historicalContext: null,
    recommendedAction: null,
    recommendedAmount: null,
    confidence: null,
    reasons: [],
    assumptions: [],
    nextTrigger: null,
    // Provenance of the fields THIS stage filled (exposure/currentProtection).
    tier,
    warnings,
    asOf,
  };
}
